import { useEffect, useState } from "react";
import { NavLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Bell, Database, Download, Info, Keyboard, ListPlus, Palette, Plug, Plus, RefreshCw, Search as SearchIcon, Trash2, User, Users } from "lucide-react";
import { KEYBOARD_SHORTCUTS, type ConnectedAccount, type ConnectedAccountProvider, type ConnectedAccountsResponse, type DarkThemeVariant, type ThemePreference, type UserNotificationSettings } from "@hub/shared";
import { Button, Field, Input, Kbd, Pill, Select, Spinner, Switch, formatDate, relativeTime, toast } from "@/ui";
import { useQuery } from "@tanstack/react-query";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { getEngine, getHost, hostMode, type HostIntegrationStatus, type HostIntegrationId, parseAccountIntegrationId, type IntegrationProviderId, type KnownHostIntegrationId } from "@/engine/transport";
import { SERVER_URL_EDITABLE } from "@/app/server-url";
import { useSession } from "@/app/session";
import { APP_ICONS, useTheme } from "@/app/theme";
import { useLayoutStore } from "@/app/navigation";
import { Page } from "@/app/layout/Page";
import { api, ApiError } from "@/api/client";
import { ConfirmModal } from "@/features/contact/ConfirmModal";
import { ImportPanel } from "./ImportPanel";
import { downloadText } from "./download";
import { buildVCards } from "./vcard";
import { FieldsSection } from "./FieldsSection";
import { webBuildLabel } from "@/features/feedback/SendFeedbackDialog";
import { ApiKeysSection, DangerZone, SessionsSection, WebhooksSection } from "./AccountSecurity";
import s from "../shared.module.css";

const SECTIONS = [
  { id: "account", label: "Account", icon: User },
  { id: "appearance", label: "Appearance", icon: Palette },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "fields", label: "Fields", icon: ListPlus },
  { id: "integrations", label: "Integrations and import", icon: Plug },
  { id: "export", label: "Export", icon: Download },
  { id: "workspace", label: "Workspace", icon: Users },
  { id: "sync", label: "Sync and data", icon: Database },
  { id: "about", label: "About and shortcuts", icon: Info },
] as const;
type SectionId = (typeof SECTIONS)[number]["id"];

export function SettingsPage() {
  const { section = "account" } = useParams();
  const current = (SECTIONS.find((x) => x.id === section)?.id ?? "account") as SectionId;
  return (
    <Page flush title="Settings">
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <nav className={s.settingsNav} aria-label="Settings sections">
          {SECTIONS.map((sec) => {
            const Icon = sec.icon;
            return (
              <NavLink key={sec.id} to={`/settings/${sec.id}`} className={s.settingsNavItem} end>
                <Icon size={14} /> {sec.label}
              </NavLink>
            );
          })}
        </nav>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 40px" }}>
          <div style={{ maxWidth: 680 }}>
            {current === "account" && <AccountSection />}
            {current === "appearance" && <AppearanceSection />}
            {current === "notifications" && <NotificationsSection />}
            {current === "fields" && <FieldsSection />}
            {current === "integrations" && <IntegrationsSection />}
            {current === "export" && <ExportSection />}
            {current === "workspace" && <WorkspaceSection />}
            {current === "sync" && <SyncSection />}
            {current === "about" && <AboutSection />}
          </div>
        </div>
      </div>
    </Page>
  );
}

function Row({ label, hint, children }: { label: string; hint?: string; children?: React.ReactNode }) {
  return (
    <div className={s.settingRow}>
      <div>
        <div className={s.settingLabel}>{label}</div>
        {hint && <div className={s.settingHint}>{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function H({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 650 }}>{children}</h2>
      {sub && <div className="u-small u-muted">{sub}</div>}
    </div>
  );
}

/** A code goes to the new address; entering it moves the account. Every signed-in device stays signed in. */
function ChangeEmailRow() {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reset = () => { setOpen(false); setEmail(""); setCode(""); setSent(false); setError(null); };
  const sendCode = async () => {
    setBusy(true); setError(null);
    try {
      const res = await api.account.requestEmailCode(email.trim());
      setSent(true);
      if (res.devCode) setCode(res.devCode);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not send the code");
    } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setError(null);
    try {
      await api.account.changeEmail({ email: email.trim(), code: code.trim() });
      await session.refreshMe();
      toast("Email changed", "success");
      reset();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not change the email");
    } finally { setBusy(false); }
  };
  if (!open) return <Row label="Change email" hint="A code goes to the new address first. Your devices stay signed in."><Button size="sm" variant="outline" onClick={() => setOpen(true)}>Change</Button></Row>;
  return (
    <Row label="Change email" hint={sent ? `Enter the code sent to ${email.trim()}` : "A code goes to the new address first. Your devices stay signed in."}>
      <span className="u-row">
        {!sent ? (
          <>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={{ width: 240 }} autoFocus />
            <Button size="sm" variant="primary" loading={busy} disabled={!email.includes("@")} onClick={sendCode}>Send code</Button>
          </>
        ) : (
          <>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" inputMode="numeric" style={{ width: 120 }} className="u-mono" autoFocus />
            <Button size="sm" variant="primary" loading={busy} disabled={code.trim().length < 4} onClick={apply}>Save</Button>
          </>
        )}
        <Button size="sm" variant="ghost" onClick={reset}>Cancel</Button>
        {error && <span className="u-small" style={{ color: "var(--danger, #d33)" }}>{error}</span>}
      </span>
    </Row>
  );
}

function AccountSection() {
  const session = useSession();
  const navigate = useNavigate();
  const settings = useEngineQuery("getUserSettings", undefined);
  const save = useEngineMutation("saveUserSettings", { invalidate: ["getUserSettings"] });
  const self = useEngineQuery("getSelfContact", undefined);
  const stats = useEngineQuery("getWorkspaceInteractionStats", undefined);
  return (
    <>
      <H sub={session.offline ? "Local only. Sign in to sync across devices and use the network." : undefined}>Account</H>
      <Row label="Email">{session.user?.email ?? "Not signed in"}</Row>
      {session.isSignedIn && <ChangeEmailRow />}
      <Row label="Name">{session.user ? [session.user.firstName, session.user.lastName].filter(Boolean).join(" ") || session.user.username : ""}</Row>
      <Row label="Your own card" hint="The person in this workspace that is you. Used for your own needs and offers.">{self.data ? <span>{self.data.profile.displayName}</span> : <span className="u-muted u-small">Not set</span>}</Row>
      <Row label="Default reminder time" hint="Used when a note mentions a day without a time">
        <Input type="time" value={settings.data?.defaultReminderTime ?? "09:00"} onChange={(e) => save.mutate({ defaultReminderTime: e.target.value })} style={{ width: 120 }} />
      </Row>
      <Row label="Detect dates in notes" hint="Offer a reminder when a note mentions a date">
        <Switch on={!(settings.data?.reminderParsingDisabled ?? false)} onChange={(v) => save.mutate({ reminderParsingDisabled: !v })} label="Detect dates in notes" />
      </Row>
      <Row label="Notes in the sidebar" hint="Show recent notes in the person preview">
        <Switch on={settings.data?.showNotesInSidebar ?? true} onChange={(v) => save.mutate({ showNotesInSidebar: v })} label="Notes in sidebar" />
      </Row>
      {stats.data && (
        <div className={s.statGrid} style={{ marginTop: 16 }}>
          {[["People", stats.data.contacts], ["Interactions", stats.data.interactions], ["Notes", stats.data.notes], ["Groups", stats.data.groups]].map(([l, v]) => (
            <div key={String(l)} className={s.stat}><div className={s.statValue}>{Number(v).toLocaleString()}</div><div className={s.statLabel}>{l}</div></div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 20 }} className="u-row">
        {session.offline ? <Button variant="primary" onClick={() => navigate("/onboarding?step=signin")}>Sign in</Button> : null}
        <Button variant="outline" onClick={() => { session.signOut(); navigate("/onboarding"); }}>Sign out</Button>
      </div>
      {session.isSignedIn && (
        <>
          <SessionsSection />
          <ApiKeysSection />
          <WebhooksSection />
          <DangerZone onDeleted={() => { session.signOut(); navigate("/onboarding"); }} />
        </>
      )}
    </>
  );
}

function AppearanceSection() {
  const theme = useTheme();
  const host = getHost();
  const [icons, setIcons] = useState<{ id: string; name: string; preview: string }[] | null>(null);
  useEffect(() => {
    host?.shell?.getAppIcons?.().then(setIcons).catch(() => setIcons(null));
  }, [host]);
  const options: { id: ThemePreference; label: string; bg: string; fg: string }[] = [
    { id: "auto", label: "Match system", bg: "linear-gradient(90deg, #fafafa 50%, #1d1e20 50%)", fg: "#888" },
    { id: "light", label: "Light", bg: "#fafafa", fg: "#1c1c1e" },
    { id: "dark", label: "Dark", bg: theme.darkTheme === "dark-black" ? "#000" : "#1d1e20", fg: "#f2f2f3" },
  ];
  return (
    <>
      <H>Appearance</H>
      <div className={s.cardGrid} style={{ gridTemplateColumns: "repeat(3, 1fr)", marginBottom: 16 }}>
        {options.map((o) => (
          <button key={o.id} type="button" className={s.themeCard} data-active={theme.theme === o.id ? "true" : undefined} onClick={() => theme.setTheme(o.id)}>
            <div className={s.themePreview} style={{ background: o.bg }} />
            {o.label}
          </button>
        ))}
      </div>
      <Row label="Dark variant" hint="Used when the dark theme is active">
        <div className={s.segmented}>
          {(["dark-graphite", "dark-black"] as DarkThemeVariant[]).map((v) => (
            <button key={v} type="button" data-active={theme.darkTheme === v ? "true" : undefined} onClick={() => theme.setDarkTheme(v)}>{v === "dark-black" ? "Black" : "Graphite"}</button>
          ))}
        </div>
      </Row>
      {hostMode() === "desktop" && (
        <Row label="App icon" hint="Shown in the Dock">
          <div className="u-row" style={{ gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {(icons ?? APP_ICONS.map((i) => ({ id: i.id, name: i.name, preview: i.color }))).map((i) => (
              <button key={i.id} type="button" className={s.iconSwatch} data-active={(theme.appIcon ?? "default") === i.id ? "true" : undefined} style={{ background: i.preview.startsWith("#") ? i.preview : undefined, backgroundImage: i.preview.startsWith("#") ? undefined : `url(${i.preview})`, backgroundSize: "cover" }} onClick={() => theme.setAppIcon(i.id === "default" ? null : i.id)} title={i.name} aria-label={i.name}>
                {i.preview.startsWith("#") ? "H" : ""}
              </button>
            ))}
          </div>
        </Row>
      )}
    </>
  );
}

const NOTIFICATION_ROWS: { key: keyof UserNotificationSettings; label: string; hint: string; group: string }[] = [
  { key: "preMeeting", label: "Before a meeting", hint: "15 minutes before, with what you know about the attendees", group: "Meetings" },
  { key: "inMeeting", label: "During a meeting", hint: "A quiet nudge to take notes", group: "Meetings" },
  { key: "postMeeting", label: "After a meeting", hint: "A prompt to write down what happened", group: "Meetings" },
  { key: "includeRecent", label: "Include people met recently", hint: "People you first met in the last 14 days", group: "Meetings" },
  { key: "includeRecurringEvents", label: "Include recurring events", hint: "Standing meetings", group: "Meetings" },
  { key: "includePeopleWithoutInformation", label: "Include people without details", hint: "Attendees Hub knows nothing about", group: "Meetings" },
  { key: "dailyBrief", label: "Daily brief", hint: "A morning summary of the people you are meeting today", group: "Digests" },
  { key: "weeklyDigest", label: "Weekly digest", hint: "What changed in your network this week", group: "Digests" },
  { key: "birthdays", label: "Birthdays", hint: "Notify on the day", group: "Alerts" },
  { key: "reconnects", label: "Reconnect suggestions", hint: "When new suggestions are ready", group: "Alerts" },
  { key: "feedReconnects", label: "Reconnects", hint: "", group: "Home feed" },
  { key: "feedBirthdays", label: "Birthdays", hint: "", group: "Home feed" },
  { key: "feedEvents", label: "Upcoming meetings", hint: "", group: "Home feed" },
  { key: "feedNews", label: "News about companies", hint: "Public headlines about the companies of your closest and starred people", group: "Home feed" },
  { key: "feedPosts", label: "Social posts", hint: "", group: "Home feed" },
  { key: "feedProfileChanges", label: "Profile changes", hint: "New titles, companies, locations", group: "Home feed" },
  { key: "feedGroupReconnects", label: "Group reconnects", hint: "", group: "Home feed" },
  { key: "feedMatches", label: "Needs and offers matches", hint: "", group: "Home feed" },
];

function NotificationsSection() {
  const settings = useEngineQuery("getUserNotificationSettings", undefined);
  const save = useEngineMutation("saveUserNotificationSettings", { invalidate: ["getUserNotificationSettings"] });
  const workspace = useEngineQuery("getWorkspaceSettings", undefined);
  const saveWorkspace = useEngineMutation("saveWorkspaceSettings", { invalidate: ["getWorkspaceSettings", "getReconnectSuggestions", "getTimeline"] });
  const userSettings = useEngineQuery("getUserSettings", undefined);
  const saveUser = useEngineMutation("saveUserSettings", { invalidate: ["getUserSettings"] });
  const scanNews = useEngineMutation("scanNews", { invalidate: ["getTimeline", "getTimelineUnreadCount"] });
  const enrichAvatars = useEngineMutation("enrichAvatars", { invalidate: ["queryContacts", "getContactRows", "getContact"] });
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  if (!settings.data) return <Spinner />;
  const groups = Array.from(new Set(NOTIFICATION_ROWS.map((r) => r.group)));
  const gravatar = userSettings.data?.metadata?.gravatar === true;
  return (
    <>
      <H sub="Hub notifies you. It never contacts the other person.">Notifications</H>
      {hostMode() === "web" && typeof Notification !== "undefined" && perm !== "granted" && (
        <Row label="Browser notifications" hint="Allow this site to show reminders while the tab is open">
          <Button size="sm" onClick={() => Notification.requestPermission().then(setPerm)}>Allow</Button>
        </Row>
      )}
      {groups.map((g) => (
        <section key={g} className={s.section}>
          <div className="u-small" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-tertiary)", margin: "12px 0 4px" }}>{g}</div>
          {g === "Alerts" && (
            <Row label="Reconnect suggestions per day" hint="How many people Hub proposes each morning (1 to 10)">
              <Input type="number" min={1} max={10} step={1} aria-label="Reconnect suggestions per day" value={workspace.data?.reconnectPerDay ?? 3} onChange={(e) => { const n = Math.min(10, Math.max(1, Math.round(Number(e.target.value) || 1))); saveWorkspace.mutate({ reconnectPerDay: n }); }} style={{ width: 80 }} data-testid="reconnect-per-day" />
            </Row>
          )}
          {NOTIFICATION_ROWS.filter((r) => r.group === g).map((r) => (
            <Row key={r.key} label={r.label} hint={r.hint || undefined}>
              <span className="u-row" style={{ gap: 6 }}>
                {r.key === "feedNews" && (
                  <Button size="sm" variant="ghost" icon={<SearchIcon size={12} />} loading={scanNews.isPending} disabled={!settings.data![r.key]} onClick={() => scanNews.mutate({ force: true }, { onSuccess: (res) => toast(res.articles > 0 ? `${res.articles} new article${res.articles === 1 ? "" : "s"} about ${res.companies} compan${res.companies === 1 ? "y" : "ies"}` : `Checked ${res.companies} compan${res.companies === 1 ? "y" : "ies"}, nothing new`) })} title="Look for news now">
                    Scan now
                  </Button>
                )}
                <Switch on={Boolean(settings.data![r.key])} onChange={(v) => save.mutate({ [r.key]: v })} label={r.label} />
              </span>
            </Row>
          ))}
          {g === "Home feed" && (
            <Row label="Find photos (Gravatar)" hint="Looks up a public Gravatar for people without a photo, using a hash of their email. Runs once a day while on.">
              <span className="u-row" style={{ gap: 6 }}>
                {gravatar && (
                  <Button size="sm" variant="ghost" icon={<SearchIcon size={12} />} loading={enrichAvatars.isPending} onClick={() => enrichAvatars.mutate({ limit: 200 }, { onSuccess: (res) => toast(res.found > 0 ? `Found ${res.found} photo${res.found === 1 ? "" : "s"} among ${res.checked} people` : `Checked ${res.checked} people, no new photos`) })}>
                    Find now
                  </Button>
                )}
                <Switch on={gravatar} onChange={(v) => saveUser.mutate({ metadata: { ...(userSettings.data?.metadata ?? {}), gravatar: v } })} label="Find photos with Gravatar" />
              </span>
            </Row>
          )}
        </section>
      ))}
    </>
  );
}

const PROVIDER_NAMES: Record<IntegrationProviderId, string> = { google: "Google", microsoft: "Microsoft 365" };

/** Name and hint for a row; accounts of a provider are named after the account. */
function integrationMeta(i: HostIntegrationStatus): { name: string; hint: string } {
  const account = parseAccountIntegrationId(i.id);
  if (account) return { name: `${PROVIDER_NAMES[account.provider]}: ${i.accountLabel ?? "account"}`, hint: "Contacts, mail metadata and calendar for this account" };
  return HOST_INTEGRATION_LABELS[i.id as KnownHostIntegrationId];
}

/** Collects the OAuth client for a provider row, then starts the first sign-in. */
function ProviderSetup({ provider, onDone }: { provider: IntegrationProviderId; onDone: (opts: Record<string, string>) => Promise<unknown> }) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const ready = clientId.trim().length > 0 && (provider === "microsoft" || clientSecret.trim().length > 0);
  return (
    <div className="u-col" style={{ gap: 6, marginTop: 8 }} data-testid={`provider-setup-${provider}`}>
      <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder={provider === "google" ? "OAuth client id" : "Application (client) id"} />
      {provider === "google" && <Input value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="OAuth client secret" type="password" />}
      <div className="u-small u-muted">
        {provider === "google"
          ? "From the Google Cloud console: a Desktop OAuth client with the People, Gmail and Calendar APIs enabled."
          : "From an Entra app registration with a mobile and desktop redirect of http://localhost."}
      </div>
      <Button size="sm" variant="primary" disabled={!ready} loading={saving} onClick={async () => { setSaving(true); try { await onDone(provider === "google" ? { clientId, clientSecret } : { clientId }); } finally { setSaving(false); } }}>Save and sign in</Button>
    </div>
  );
}

const HOST_INTEGRATION_LABELS: Record<KnownHostIntegrationId, { name: string; hint: string }> = {
  apple_contacts: { name: "Apple Contacts", hint: "Names, emails, phones, birthdays" },
  apple_messages: { name: "Messages", hint: "Per-day message counts only. Message content is never read or stored." },
  apple_calls: { name: "Phone and FaceTime", hint: "Call history (who and when)" },
  apple_calendar: { name: "Calendar", hint: "Events and attendees" },
  apple_mail: { name: "Mail", hint: "Who you emailed and when, from the Mail app. People you write to are added; message bodies are never read." },
  google: { name: "Google", hint: "Contacts, Gmail and Calendar with your own OAuth client. People you email are added." },
  microsoft: { name: "Microsoft", hint: "Outlook contacts, mail and calendar. People you email are added." },
  csv: { name: "CSV file", hint: "Any spreadsheet export" },
  linkedin_csv: { name: "LinkedIn connections", hint: "The Connections.csv export from LinkedIn" },
  ics: { name: "Calendar file (ICS)", hint: "Events from an .ics export" },
};

/**
 * Google and Microsoft 365 accounts connected through the Hub server: the server runs the sign-in, keeps the tokens
 * and pulls contacts, mail and calendar for the workspace, so a phone or a browser gets them without a Mac. The Mac
 * app can still connect accounts locally (below); those stay on the Mac.
 */
function ConnectedAccountsSection() {
  const session = useSession();
  const host = getHost();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<ConnectedAccountsResponse | null | undefined>(undefined);
  const [busy, setBusy] = useState<string | null>(null);
  const load = () => {
    if (!session.isSignedIn || !session.workspaceId) return setData(null);
    api.integrations.accounts().then(setData).catch((e) => setData(e instanceof ApiError && e.status === 404 ? null : { accounts: [], providers: { google: { configured: false }, microsoft: { configured: false } } }));
  };
  useEffect(load, [session.isSignedIn, session.workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // The provider sends the browser back here with ?connected=1 (or ?error=...); the Mac app returns by deep link and refreshes on focus.
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");
    const provider = searchParams.get("provider");
    if (!connected && !error) return;
    const name = provider === "microsoft" ? "Microsoft 365" : "Google";
    if (connected) toast(`${name} is connected. The first pull is running on the server.`, "success");
    else toast(error === "cancelled" ? `${name} sign-in was cancelled.` : `${name} could not be connected (${error}).`, "danger");
    const next = new URLSearchParams(searchParams);
    for (const k of ["connected", "error", "provider", "account"]) next.delete(k);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  // A pull runs on the server after Sync answers; look again every few seconds until it is done.
  const syncingNow = data?.accounts.some((a) => a.status === "syncing") ?? false;
  useEffect(() => {
    if (!syncingNow) return;
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [syncingNow]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  if (data === undefined || data === null) return null;
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await fn();
      load();
    } catch (e) {
      toast(e instanceof Error ? e.message : "That did not work", "danger");
    } finally {
      setBusy(null);
    }
  };
  const connect = (provider: ConnectedAccountProvider, loginHint: string | null = null) =>
    run(`connect:${provider}`, async () => {
      const desktop = hostMode() === "desktop" && !!host?.shell;
      const { url } = await api.integrations.start(provider, { workspaceId: session.workspaceId!, returnTo: desktop ? "app" : "web", loginHint });
      if (desktop) {
        await host!.shell!.openExternal(url);
        toast("Finish the sign-in in your browser; this page updates when you come back.");
      } else {
        window.location.assign(url);
      }
    });
  const names: Record<ConnectedAccountProvider, string> = { google: "Google", microsoft: "Microsoft 365" };
  const hints: Record<ConnectedAccountProvider, string> = { google: "Gmail, Google Calendar and Contacts", microsoft: "Outlook mail, calendar and people" };
  const accountHint = (a: ConnectedAccount) => (a.status === "syncing" ? (a.progress ? `Pulling now: ${a.progress}` : "Pulling now") : a.lastError ? a.lastError : a.lastSync ? `Last pulled ${relativeTime(a.lastSync)}` : "Connected. The first pull is on its way.");
  return (
    <div style={{ marginBottom: 20 }} data-testid="connected-accounts">
      <H sub="Connected through your Hub server, so your phone, this browser and the Mac app all receive the results. Read only; nothing is sent on your behalf.">Accounts</H>
      {(["google", "microsoft"] as const).map((provider) => {
        const mine = data.accounts.filter((a) => a.provider === provider);
        const configured = data.providers[provider]?.configured;
        return (
          <div key={provider}>
            <Row label={names[provider]} hint={configured ? hints[provider] : `${hints[provider]}. Not set up on this server yet.`}>
              {configured && <Button size="sm" variant="primary" icon={<Plus size={12} />} loading={busy === `connect:${provider}`} onClick={() => connect(provider)} data-testid={`connect-${provider}`}>{mine.length ? "Add account" : "Connect"}</Button>}
            </Row>
            {mine.map((a) => (
              <Row key={a.id} label={a.email ?? `${names[provider]} account`} hint={accountHint(a)}>
                <div className="u-row" style={{ gap: 6 }}>
                  <Pill tone={a.status === "error" ? "danger" : a.status === "reconnect" ? "warning" : "success"}>{a.status === "error" ? "Error" : a.status === "reconnect" ? "Connect again" : a.status === "syncing" ? "Syncing" : "Connected"}</Pill>
                  {a.status === "reconnect" ? (
                    <Button size="sm" variant="primary" icon={<RefreshCw size={12} />} loading={busy === `connect:${provider}`} onClick={() => connect(provider, a.email)} data-testid={`reconnect-${a.id}`}>Connect again</Button>
                  ) : (
                    <Button size="sm" icon={<RefreshCw size={12} />} loading={busy === `sync:${a.id}` || a.status === "syncing"} onClick={() => run(`sync:${a.id}`, () => api.integrations.sync(a.id))}>Sync</Button>
                  )}
                  <Button size="sm" variant="ghost" icon={<Trash2 size={12} />} onClick={() => { if (window.confirm(`Disconnect ${a.email ?? names[provider]}? Imported data stays unless you choose to delete it next.`)) { const del = window.confirm("Also delete the data it imported?"); run(`remove:${a.id}`, () => api.integrations.disconnect(a.id, del)); } }}>Disconnect</Button>
                </div>
              </Row>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function IntegrationsSection() {
  const host = getHost();
  const desktop = hostMode() === "desktop" && !!host?.integrations;
  const [list, setList] = useState<HostIntegrationStatus[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [setup, setSetup] = useState<IntegrationProviderId | null>(null);
  const [calendarPicker, setCalendarPicker] = useState<string | null>(null);
  const engineIntegrations = useEngineQuery("getIntegrations", undefined);
  const counts = useEngineQuery("getIntegrationConnectionCounts", undefined);
  const refresh = () => host?.integrations?.list().then(setList).catch(() => setList([]));
  useEffect(() => {
    if (!desktop) return;
    refresh();
    return host!.integrations!.onStatus((st) => setList((l) => (l ? l.map((x) => (x.id === st.id ? st : x)) : l)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktop]);
  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    try {
      await fn();
      refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : "That did not work", "danger");
    } finally {
      setBusy(null);
    }
  };
  const needsAttention = (engineIntegrations.data ?? []).filter((i) => i.status === "needs_attention" || i.status === "error");
  return (
    <>
      <ConnectedAccountsSection />
      <H sub={desktop ? "These run on this Mac, read only. Hub never sends anything on your behalf." : "What this workspace receives from each source."}>{desktop ? "On this Mac" : "Sources"}</H>
      {needsAttention.length > 0 && (
        <div style={{ padding: 10, border: "1px solid var(--warning)", background: "var(--warning-soft)", borderRadius: 8, marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>Needs attention</div>
          {needsAttention.map((i) => <div key={i.id} className="u-small">{i.name ?? i.provider}: {i.lastError ?? i.status}</div>)}
        </div>
      )}
      {desktop ? (
        <>
          {list === null && <Spinner />}
          {(list ?? []).filter((i) => i.available && !["csv", "linkedin_csv", "ics"].includes(i.id)).map((i) => {
            const meta = integrationMeta(i);
            const providerRow = i.id === "google" || i.id === "microsoft" ? (i.id as IntegrationProviderId) : null;
            if (providerRow) {
              const n = i.accountCount ?? 0;
              const hint = i.configured ? (n === 0 ? "Set up. Add the first account to start syncing." : `${n} ${n === 1 ? "account" : "accounts"} connected${i.error ? `. ${i.error}` : ""}`) : meta.hint;
              return (
                <Row key={i.id} label={meta.name} hint={hint}>
                  <div className="u-col" style={{ alignItems: "flex-end", gap: 4 }}>
                    <div className="u-row" style={{ gap: 6 }}>
                      {!i.configured && <Button size="sm" variant="primary" onClick={() => setSetup(setup === providerRow ? null : providerRow)}>Set up</Button>}
                      {i.configured && <Button size="sm" variant="primary" icon={<Plus size={12} />} loading={busy === i.id} onClick={() => run(i.id, () => host!.integrations!.authorize(i.id))}>Add account</Button>}
                      {i.configured && <Button size="sm" variant="ghost" icon={<Trash2 size={12} />} onClick={() => { if (window.confirm(`Forget the ${meta.name} client and disconnect ${n ? "its accounts" : "it"}? Imported data stays unless you choose to delete it next.`)) { const del = n ? window.confirm("Also delete the data those accounts imported?") : false; run(i.id, () => host!.integrations!.remove(i.id, del)); } }}>Forget</Button>}
                    </div>
                    {setup === providerRow && !i.configured && <ProviderSetup provider={providerRow} onDone={(opts) => run(i.id, () => host!.integrations!.authorize(i.id, opts)).then(() => setSetup(null))} />}
                  </div>
                </Row>
              );
            }
            const calendars = i.calendars ?? [];
            const hidden = calendars.filter((c) => !c.enabled).length;
            const hint = i.error ?? (i.lastSync ? `Last synced ${relativeTime(i.lastSync)}${calendars.length ? `, ${calendars.length - hidden} of ${calendars.length} calendars` : ""}` : meta.hint);
            return (
              <Row key={i.id} label={meta.name} hint={hint}>
                <div className="u-col" style={{ alignItems: "flex-end", gap: 4 }}>
                <div className="u-row" style={{ gap: 6 }}>
                  {calendars.length > 0 && host!.integrations!.setOptions && (
                    <Button size="sm" variant="ghost" onClick={() => setCalendarPicker(calendarPicker === i.id ? null : i.id)}>Calendars</Button>
                  )}
                  {i.authorized ? <Pill tone={i.error ? "danger" : "success"}>{i.error ? "Error" : i.syncing ? "Syncing" : "Connected"}</Pill> : null}
                  {i.requiresDiskAccess && !i.hasDiskAccess && <Button size="sm" variant="outline" onClick={() => host!.integrations!.openDiskAccessSettings()}>Grant disk access</Button>}
                  {!i.authorized ? (
                    <Button size="sm" variant="primary" loading={busy === i.id} onClick={() => run(i.id, () => host!.integrations!.authorize(i.id))}>{i.provider ? "Reconnect" : "Connect"}</Button>
                  ) : (
                    <>
                      <Button size="sm" icon={<RefreshCw size={12} />} loading={busy === i.id || i.syncing} onClick={() => run(i.id, () => host!.integrations!.sync(i.id))}>Sync</Button>
                      <Button size="sm" variant="ghost" icon={<Trash2 size={12} />} onClick={() => { if (window.confirm(`Remove ${meta.name}? Imported data stays unless you choose to delete it next.`)) { const del = window.confirm("Also delete the data it imported?"); run(i.id, () => host!.integrations!.remove(i.id, del)); } }}>Remove</Button>
                    </>
                  )}
                </div>
                {calendarPicker === i.id && calendars.length > 0 && (
                  <div className="u-col" style={{ gap: 4, marginTop: 6, maxHeight: 220, overflowY: "auto" }} data-testid="calendar-picker">
                    {calendars.map((c) => (
                      <label key={c.id} className="u-row u-small" style={{ gap: 6, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={c.enabled}
                          onChange={(e) => {
                            const excluded = calendars.filter((x) => (x.id === c.id ? !e.target.checked : !x.enabled)).map((x) => x.name);
                            run(i.id, async () => { await host!.integrations!.setOptions!(i.id, { excluded }); toast("Calendar list saved. The next sync applies it.", "success"); });
                          }}
                        />
                        {c.name}
                      </label>
                    ))}
                  </div>
                )}
                </div>
              </Row>
            );
          })}
        </>
      ) : (
        <>
          {(engineIntegrations.data ?? []).map((i) => {
            const c = counts.data?.find((x) => x.integrationId === i.id);
            return (
              <Row key={i.id} label={i.name ?? i.provider} hint={`${i.capability}${c ? `, ${c.contacts} people, ${c.interactions} interactions` : ""}${i.lastSync ? `, synced ${relativeTime(i.lastSync)}` : ""}`}>
                <Pill tone={i.status === "active" ? "success" : i.status === "needs_attention" || i.status === "error" ? "danger" : "neutral"}>{i.status.replace(/_/g, " ")}</Pill>
              </Row>
            );
          })}
          <div className="u-small u-muted" style={{ margin: "8px 0 16px" }}>Apple Contacts, Messages, Calendar and call history connect from the Mac app; the phone supplies its Contacts and Calendar. Here you can connect Google and Microsoft 365 above and import files below.</div>
        </>
      )}
      <ImportPanel />
    </>
  );
}

/** Builds the vCard file on the client: rows for the primary email and phone, profiles for names, work and birthday, notes counted per person. */
async function exportVCards(): Promise<string> {
  const engine = await getEngine();
  const rows: Awaited<ReturnType<typeof engine.invoke<"queryContacts">>>["items"] = [];
  let offset = 0;
  for (;;) {
    const page = await engine.invoke("queryContacts", { limit: 500, offset, sort: "name", direction: "asc" });
    rows.push(...page.items);
    if (page.nextOffset === null || page.items.length === 0) break;
    offset = page.nextOffset;
  }
  const profiles = new Map<string, Awaited<ReturnType<typeof engine.invoke<"getContactProfiles">>>[number]>();
  for (let i = 0; i < rows.length; i += 200) {
    for (const p of await engine.invoke("getContactProfiles", { contactIds: rows.slice(i, i + 200).map((r) => r.contactId) })) profiles.set(p.contactId, p);
  }
  const notesCount = new Map<string, number>();
  let noteOffset = 0;
  for (;;) {
    const page = await engine.invoke("getAllNotes", { limit: 1000, offset: noteOffset });
    for (const n of page.items) if (n.contactId) notesCount.set(n.contactId, (notesCount.get(n.contactId) ?? 0) + 1);
    if (page.nextOffset === null || page.items.length === 0) break;
    noteOffset = page.nextOffset;
  }
  return buildVCards(rows.map((row) => ({ row, profile: profiles.get(row.contactId) ?? null, notesCount: notesCount.get(row.contactId) ?? 0 })));
}

function ExportSection() {
  const exp = useEngineMutation("exportUserData", { invalidate: false });
  const [vcfBusy, setVcfBusy] = useState(false);
  const date = new Date().toISOString().slice(0, 10);
  const downloadVcf = async () => {
    setVcfBusy(true);
    try {
      const vcf = await exportVCards();
      await downloadText(`hub-people-${date}.vcf`, vcf, "text/vcard");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not build the vCard file", "danger");
    } finally {
      setVcfBusy(false);
    }
  };
  return (
    <>
      <H sub="Your data is yours. Download it whenever you like.">Export</H>
      <Row label="People (CSV)" hint="Name, email, phone, company, title, location"><Button size="sm" icon={<Download size={13} />} loading={exp.isPending} onClick={() => exp.mutate({ format: "csv" }, { onSuccess: (r) => r.contactsCsv && downloadText(`hub-people-${date}.csv`, r.contactsCsv, "text/csv") })}>Download</Button></Row>
      <Row label="People (vCard .vcf)" hint="Opens in Apple Contacts, Google Contacts and Outlook. Name, email, phone, company, title, websites, birthday and a note with the notes count."><Button size="sm" icon={<Download size={13} />} loading={vcfBusy} onClick={downloadVcf} data-testid="export-vcf">Download</Button></Row>
      <Row label="Notes (CSV)" hint="Every note with its person and label"><Button size="sm" icon={<Download size={13} />} loading={exp.isPending} onClick={() => exp.mutate({ format: "csv" }, { onSuccess: (r) => r.notesCsv && downloadText(`hub-notes-${date}.csv`, r.notesCsv, "text/csv") })}>Download</Button></Row>
      <Row label="Everything (JSON)" hint="Full export of this workspace"><Button size="sm" icon={<Download size={13} />} loading={exp.isPending} onClick={() => exp.mutate({ format: "json" }, { onSuccess: (r) => r.json && downloadText(`hub-export-${date}.json`, r.json, "application/json") })}>Download</Button></Row>
    </>
  );
}

function WorkspaceSection() {
  const session = useSession();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteSent, setInviteSent] = useState<string | null>(null);
  const [creating, setCreating] = useState(params.get("new") === "1");
  const create = () => {
    if (!name.trim()) return;
    void session.createWorkspace(name.trim()).then(() => { toast("Workspace created", "success"); window.location.reload(); }, (e) => toast(e instanceof Error ? e.message : "Could not create the workspace.", "danger"));
  };
  // Who is in a shared workspace is the server's to say; this device only knows about itself until then.
  const online = !session.offline && !!session.token && !!session.workspaceId;
  const serverMembers = useQuery({ queryKey: ["workspace-members", session.workspaceId], queryFn: () => api.workspaces.members(session.workspaceId!), enabled: online });
  const localMembers = useEngineQuery("listWorkspaceMembers", undefined);
  const members = online && serverMembers.data ? { data: serverMembers.data.members, isLoading: false } : online ? { data: [], isLoading: serverMembers.isLoading } : localMembers;
  const local = useEngineQuery("listLocalWorkspaces", undefined);
  const adopt = useEngineMutation("adoptWorkspace");
  const others = (local.data ?? []).filter((w) => !w.isCurrent && w.contacts > 0);
  const ws = session.workspaces.find((w) => w.id === session.workspaceId);
  const invite = async () => {
    if (!session.workspaceId || !inviteEmail.includes("@")) return;
    try {
      const res = await api.workspaces.invite(session.workspaceId, { email: inviteEmail.trim() });
      setInviteLink(res.link);
      setInviteSent(res.sent ? inviteEmail.trim() : null);
      setInviteEmail("");
      void serverMembers.refetch();
      toast(res.sent ? `Invitation emailed to ${inviteEmail.trim()}` : "Invitation ready. Send the link yourself.", "success");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Could not create the invite", "danger");
    }
  };
  return (
    <>
      <H>Workspace</H>
      <Row label="Current workspace">{ws?.name ?? "Personal"} <Pill>{ws?.role ?? "owner"}</Pill></Row>
      <Row label="Switch">
        <Select value={session.workspaceId ?? ""} onChange={(e) => { session.setWorkspace(e.target.value); window.location.reload(); }} style={{ width: 200 }}>
          {session.workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </Select>
      </Row>
      {others.map((w) => (
        <Row key={w.id} label="Data on this device" hint={`${w.contacts} people are stored under "${w.name}", a workspace from before you signed in.`}>
          <Button size="sm" variant="primary" loading={adopt.isPending} onClick={() => adopt.mutate({ fromWorkspaceId: w.id }, { onSuccess: (r) => { toast(`Moved ${r.contacts} people into ${ws?.name ?? "this workspace"}`, "success"); session.clearPendingLocalWorkspace(); } })}>Move into {ws?.name ?? "this workspace"}</Button>
        </Row>
      ))}
      <Row label="New workspace" hint="Separate people, groups and notes. Teams share a workspace.">
        {creating ? (
          <span className="u-row">
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="Name" style={{ width: 180 }} />
            <Button size="sm" variant="primary" disabled={!name.trim()} onClick={create}>Create</Button>
          </span>
        ) : (
          <Button size="sm" onClick={() => setCreating(true)}>Create</Button>
        )}
      </Row>
      <section className={s.section} style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Members</div>
        {members.isLoading && <Spinner />}
        {(members.data ?? []).map((m) => (
          <div key={m.userId} className="u-row" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
            <span className="u-grow">{[m.user.firstName, m.user.lastName].filter(Boolean).join(" ") || m.user.username} <span className="u-small u-muted">{m.user.email}</span></span>
            <Pill>{m.role}</Pill>
          </div>
        ))}
        {!session.offline && ws?.role === "member" && <div className="u-small u-muted" style={{ marginTop: 8 }}>Only the owner or an admin of {ws.name} can invite people to it.</div>}
        {!session.offline && ws?.role !== "member" && (
          <div className="u-row" style={{ marginTop: 10 }}>
            <Input placeholder="Invite by email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && invite()} />
            <Button size="sm" onClick={invite} disabled={!inviteEmail.includes("@")}>Invite</Button>
          </div>
        )}
        {session.offline && <div className="u-small u-muted" style={{ marginTop: 8 }}>Sign in to invite people to your workspace.</div>}
        {inviteLink && (
          <div style={{ marginTop: 8 }}>
            <div className="u-small u-muted">{inviteSent ? `Emailed to ${inviteSent}. Only that address can join, once. You can also send the link:` : "The email did not go out. Send this link yourself; only the invited address can join, once:"}</div>
            <div className="u-row" style={{ marginTop: 4 }}>
              <span className="u-small u-mono u-grow" style={{ wordBreak: "break-all" }}>{inviteLink}</span>
              <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(inviteLink).then(() => toast("Link copied", "success"))}>Copy link</Button>
            </div>
          </div>
        )}
      </section>
      {!session.offline && <InviteToHub />}
    </>
  );
}

/** Invite someone to Hub itself (not to this workspace), as the iPhone does under Settings, Workspace. */
function InviteToHub() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<{ email: string; link: string | null; sent: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const to = email.trim();
    if (!to.includes("@") || busy) return;
    setBusy(true);
    try {
      const res = await api.invites.toHub({ email: to });
      setResult({ email: to, link: res.link ?? null, sent: res.sent === true });
      setEmail("");
      toast(res.sent ? `Invitation emailed to ${to}` : "Invitation ready. Send the link yourself.", "success");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Could not send the invitation", "danger");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={s.section} style={{ marginTop: 16 }} data-testid="invite-to-hub">
      <div style={{ fontWeight: 600, marginBottom: 2 }}>Invite to Hub</div>
      <div className="u-small u-muted">Hub emails them an invitation from you, with a link to join. Use an address you know is theirs.</div>
      <div className="u-row" style={{ marginTop: 10 }}>
        <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
        <Button size="sm" onClick={send} loading={busy} disabled={!email.includes("@")}>Invite</Button>
      </div>
      {result && (
        <div style={{ marginTop: 8 }}>
          <div className="u-small u-muted">{result.sent ? `Invitation emailed to ${result.email}.${result.link ? " The link, if they ask:" : ""}` : result.link ? "The email did not go out. Send this link yourself:" : "Invitation recorded."}</div>
          {result.link && (
            <div className="u-row" style={{ marginTop: 4 }}>
              <span className="u-small u-mono u-grow" style={{ wordBreak: "break-all" }}>{result.link}</span>
              <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(result.link!).then(() => toast("Link copied", "success"))}>Copy link</Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function SyncSection() {
  const session = useSession();
  const [url, setUrl] = useState(session.serverUrl);
  const [confirmReset, setConfirmReset] = useState(false);
  const status = useEngineQuery("getSyncStatus", undefined, { refetchInterval: 10_000 });
  const syncNow = useEngineMutation("syncNow", { invalidate: ["getSyncStatus"] });
  const reset = useEngineMutation("resetLocalData");
  const [health, setHealth] = useState<string | null>(null);
  const check = async () => {
    try {
      await api.health();
      setHealth("Server reachable");
    } catch (e) {
      setHealth(e instanceof ApiError ? `Cannot reach server: ${e.message}` : "Cannot reach server");
    }
  };
  return (
    <>
      <H sub="Sync is optional. Without a server everything stays on this device.">Sync and data</H>
      {SERVER_URL_EDITABLE ? (
        <Row label="Server URL" hint="Development build: where your workspace syncs. Change requires reload.">
          <span className="u-row">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} style={{ width: 240 }} className="u-mono" />
            <Button size="sm" onClick={() => { session.setServerUrl(url); toast("Server saved; reloading"); setTimeout(() => window.location.reload(), 400); }} disabled={url === session.serverUrl}>Save</Button>
            <Button size="sm" variant="ghost" onClick={check}>Test</Button>
          </span>
        </Row>
      ) : (
        <Row label="Server" hint="Where your workspace syncs.">
          <span className="u-row">
            <span className="u-mono u-small">{session.serverUrl.replace(/^https?:\/\//, "")}</span>
            <Button size="sm" variant="ghost" onClick={check}>Test</Button>
          </span>
        </Row>
      )}
      {health && <div className="u-small u-muted" style={{ padding: "4px 0" }}>{health}</div>}
      <Row label="Status" hint={status.data ? `${status.data.pendingOps} pending changes${status.data.lastPullAt ? `, last pull ${relativeTime(status.data.lastPullAt)}` : ""}${status.data.error && status.data.phase !== "error" ? `, ${status.data.error}` : ""}` : undefined}>
        <span className="u-row">
          <Pill tone={session.offline ? "neutral" : status.data?.phase === "error" ? "danger" : status.data?.phase === "idle" ? "success" : "accent"}>{session.offline ? "local only" : status.data?.phase.replace(/_/g, " ") ?? "unknown"}</Pill>
          <Button size="sm" icon={<RefreshCw size={12} />} loading={syncNow.isPending} disabled={session.offline} onClick={() => syncNow.mutate(undefined, { onSuccess: (st) => (st.phase === "error" || st.phase === "offline" ? toast(st.error ?? "Sync did not finish", "danger") : toast("Synced", "success")) })}>Sync now</Button>
        </span>
      </Row>
      {!session.offline && status.data?.phase === "error" && status.data.error && (
        <div className="u-small" style={{ color: "var(--danger)", padding: "4px 0" }} data-testid="sync-error">
          {status.data.error}
          {status.data.errorCode === "account_suspended" && " Everything on this device stays here and keeps working. Once the account is restored, sign in again to sync."}
        </div>
      )}
      <Row label="Reset local data" hint="Deletes the database on this device. Synced workspaces download again on the next start.">
        <Button size="sm" variant="danger" icon={<Trash2 size={12} />} onClick={() => setConfirmReset(true)}>Reset</Button>
      </Row>
      <ConfirmModal open={confirmReset} title="Reset local data?" body={session.offline ? "This workspace is local only, so everything in it will be gone for good. Export first if you want to keep it." : "The local copy is removed and rebuilt from the server. Unsynced changes are lost."} confirmLabel="Reset" danger loading={reset.isPending} onClose={() => setConfirmReset(false)} onConfirm={() => reset.mutate({ confirm: true }, { onSuccess: () => { setConfirmReset(false); toast("Local data reset"); window.location.reload(); } })} />
    </>
  );
}

export function ShortcutList() {
  const groups = Array.from(new Set(KEYBOARD_SHORTCUTS.map((k) => k.group)));
  return (
    <div className={s.cardGrid} style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
      {groups.map((g) => (
        <div key={g}>
          <div className="u-small" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-tertiary)", margin: "8px 0 4px" }}>{g}</div>
          {KEYBOARD_SHORTCUTS.filter((k) => k.group === g).map((k) => (
            <div key={k.title} className="u-row" style={{ justifyContent: "space-between", padding: "4px 0" }}>
              <span>{k.title}</span>
              <span className="u-row" style={{ gap: 3 }}>{k.keys.map((key, i) => <Kbd key={i}>{key === "Mod" ? (navigator.platform.includes("Mac") ? "Cmd" : "Ctrl") : key}</Kbd>)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function AboutSection() {
  const host = getHost();
  const [version, setVersion] = useState<string | null>(null);
  const setShortcutsOpen = useLayoutStore((st) => st.setShortcutsOpen);
  useEffect(() => {
    host?.shell?.getVersion?.().then(setVersion).catch(() => undefined);
  }, [host]);
  return (
    <>
      <H>About</H>
      <Row label="Hub" hint="A local-first relationship manager with needs and offers matching.">{version ?? (hostMode() === "web" ? webBuildLabel() : null) ?? `${hostMode()} build`}</Row>
      <Row label="Privacy" hint="Hub never messages anyone or acts on your behalf. From Messages, Calls and Mail it keeps only who and when, never the content. Messages you send to people on Hub are kept so you can both read the conversation. Needs and offers reach only the people you choose: private, your connections, or the Hub network." />
      <div className="u-row" style={{ margin: "16px 0 8px", justifyContent: "space-between" }}>
        <span className="u-row" style={{ gap: 6, fontWeight: 600 }}><Keyboard size={14} /> Keyboard shortcuts</span>
        <Button size="sm" variant="ghost" onClick={() => setShortcutsOpen(true)}>Open overlay (?)</Button>
      </div>
      <ShortcutList />
    </>
  );
}

export { formatDate };
